import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import './AudioDeviceCheck.css'

interface AudioDeviceCheckProps {
  show: boolean
  onClose: () => void
}

type StepIndex = 0 | 1 | 2

const STORAGE_INPUT = 'policytools_audio_input'
const STORAGE_OUTPUT = 'policytools_audio_output'
const METER_SEGMENTS = 16

// Firefox is the recommended browser for PolicyDial: on some Windows machines
// the Intel Smart Sound (SST) driver mutes all audio when Chrome/Edge grab the
// mic in "communications" mode; Firefox uses a different audio role and avoids it.
const isRecommendedBrowser = () => {
  const ua = navigator.userAgent
  return /Firefox/.test(ua) && !/Seamonkey/i.test(ua)
}

const browserLabel = () => {
  const ua = navigator.userAgent
  if (/Firefox/.test(ua)) return 'Firefox — recommended'
  if (/Edg/.test(ua)) return 'Microsoft Edge — Firefox works best'
  if (/Chrome|Chromium/.test(ua)) return 'Chrome — Firefox works best'
  if (/Safari/.test(ua)) return 'Safari — Firefox works best'
  return 'Unknown browser — Firefox works best'
}

const supportsSinkId = () =>
  typeof (HTMLMediaElement.prototype as unknown as {setSinkId?: unknown}).setSinkId === 'function'

// Step-by-step instructions to unblock the mic — the browser won't let us do it
// programmatically, so we point the agent to the exact control for their browser.
const permissionUnblockSteps = (): string[] => {
  const ua = navigator.userAgent
  if (/Firefox/.test(ua)) {
    return [
      'Click the 🎤 / 🛡 icon on the left of the address bar',
      'Remove the “Blocked” state for the microphone',
      'Reload the page, then press Try again',
    ]
  }
  if (/Edg/.test(ua)) {
    return [
      'Click the 🔒 lock icon to the left of the address bar',
      'Open “Permissions for this site” → set Microphone to Allow',
      'Press Try again',
    ]
  }
  return [
    'Click the 🔒 lock icon to the left of the address bar',
    'Open “Site settings” → set Microphone to Allow',
    'Press Try again',
  ]
}

const AudioDeviceCheck = ({show, onClose}: AudioDeviceCheckProps) => {
  const [step, setStep] = useState<StepIndex>(0)

  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([])
  const [outputs, setOutputs] = useState<MediaDeviceInfo[]>([])
  const [selectedInput, setSelectedInput] = useState<string>(
    () => localStorage.getItem(STORAGE_INPUT) || ''
  )
  const [selectedOutput, setSelectedOutput] = useState<string>(
    () => localStorage.getItem(STORAGE_OUTPUT) || ''
  )

  const [micPermission, setMicPermission] = useState<'granted' | 'prompt' | 'denied' | 'unknown'>(
    'unknown'
  )
  const [micLevelDetected, setMicLevelDetected] = useState(false)
  const [micConfirmed, setMicConfirmed] = useState<boolean | null>(null)
  const [speakerConfirmed, setSpeakerConfirmed] = useState<boolean | null>(null)

  const [recording, setRecording] = useState(false)
  const [playingBack, setPlayingBack] = useState(false)
  const [recordingDone, setRecordingDone] = useState(false)
  const [tonePlaying, setTonePlaying] = useState(false)

  // ── Refs for live audio plumbing (kept out of render) ──
  const streamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const rafRef = useRef<number | null>(null)
  const segRefs = useRef<Array<HTMLSpanElement | null>>([])
  const recorderRef = useRef<MediaRecorder | null>(null)
  const recordedChunks = useRef<Blob[]>([])
  const playbackAudioRef = useRef<HTMLAudioElement | null>(null)

  // ── Teardown of all live audio ──
  const stopMeter = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    analyserRef.current = null
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => undefined)
      audioCtxRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }, [])

  const paintMeter = useCallback((level: number) => {
    const lit = Math.round(level * METER_SEGMENTS)
    for (let i = 0; i < METER_SEGMENTS; i++) {
      const el = segRefs.current[i]
      if (!el) continue
      let cls = 'audio-check__seg'
      if (i < lit) {
        if (i >= METER_SEGMENTS - 2) cls += ' audio-check__seg--peak'
        else if (i >= METER_SEGMENTS - 6) cls += ' audio-check__seg--hot'
        else cls += ' audio-check__seg--on'
      }
      el.className = cls
    }
    if (level > 0.12) setMicLevelDetected(true)
  }, [])

  // ── Start (or restart) the mic stream + analyser for a given device ──
  const startMic = useCallback(
    async (deviceId?: string) => {
      stopMeter()
      try {
        const constraints: MediaStreamConstraints = {
          audio: deviceId ? {deviceId: {exact: deviceId}} : true,
        }
        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        streamRef.current = stream
        setMicPermission('granted')

        const Ctx =
          window.AudioContext ||
          (window as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext
        const ctx = new Ctx()
        audioCtxRef.current = ctx
        const source = ctx.createMediaStreamSource(stream)
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 512
        source.connect(analyser)
        analyserRef.current = analyser

        const data = new Uint8Array(analyser.fftSize)
        const loop = () => {
          const active = analyserRef.current
          if (!active) return
          active.getByteTimeDomainData(data)
          let sum = 0
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128
            sum += v * v
          }
          const level = Math.min(1, Math.sqrt(sum / data.length) * 3.2)
          paintMeter(level)
          rafRef.current = requestAnimationFrame(loop)
        }
        rafRef.current = requestAnimationFrame(loop)
      } catch (err) {
        const name = (err as DOMException)?.name
        setMicPermission(
          name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'prompt'
        )
      }
    },
    [paintMeter, stopMeter]
  )

  // ── Explicitly ask the browser for mic access (triggers the prompt) ──
  const requestPermission = useCallback(() => {
    void startMic(selectedInput || undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMic, selectedInput])

  // ── Enumerate devices (labels appear only after permission) ──
  const refreshDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      setInputs(devices.filter((d) => d.kind === 'audioinput'))
      setOutputs(devices.filter((d) => d.kind === 'audiooutput'))
    } catch (err) {
      // ignore — keep whatever we have
    }
  }, [])

  // ── On open: query permission, start mic, enumerate ──
  useEffect(() => {
    if (!show) return
    let cancelled = false

    const init = async () => {
      try {
        const status = await (
          navigator.permissions as unknown as {
            query: (d: {name: string}) => Promise<{
              state: string
              onchange: (() => void) | null
            }>
          }
        ).query({name: 'microphone'})
        const apply = () => {
          if (!cancelled) setMicPermission(status.state as 'granted' | 'prompt' | 'denied')
        }
        apply()
        status.onchange = apply
      } catch {
        // permissions API or 'microphone' name unsupported (e.g. Firefox) — resolve via getUserMedia
      }
      await startMic(selectedInput || undefined)
      if (!cancelled) await refreshDevices()
    }
    void init()

    return () => {
      cancelled = true
      stopMeter()
      if (playbackAudioRef.current) {
        playbackAudioRef.current.pause()
        playbackAudioRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  // ── React to mic device change ──
  const handleInputChange = useCallback(
    (deviceId: string) => {
      setSelectedInput(deviceId)
      localStorage.setItem(STORAGE_INPUT, deviceId)
      setMicLevelDetected(false)
      setMicConfirmed(null)
      void startMic(deviceId || undefined)
    },
    [startMic]
  )

  const handleOutputChange = useCallback((deviceId: string) => {
    setSelectedOutput(deviceId)
    localStorage.setItem(STORAGE_OUTPUT, deviceId)
    setSpeakerConfirmed(null)
  }, [])

  // ── Speaker test tone (routed to the selected output when supported) ──
  const playTestTone = useCallback(async () => {
    setTonePlaying(true)
    try {
      const Ctx =
        window.AudioContext ||
        (window as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext
      const ctx = new Ctx()
      const dest = ctx.createMediaStreamDestination()
      const now = ctx.currentTime
      ;[
        [660, 0],
        [880, 0.18],
      ].forEach(([freq, at]) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = freq
        osc.connect(gain)
        gain.connect(dest)
        gain.connect(ctx.destination)
        gain.gain.setValueAtTime(0, now + at)
        gain.gain.linearRampToValueAtTime(0.18, now + at + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.34)
        osc.start(now + at)
        osc.stop(now + at + 0.36)
      })

      if (selectedOutput && supportsSinkId()) {
        const audio = new Audio()
        audio.srcObject = dest.stream
        await (audio as unknown as {setSinkId: (id: string) => Promise<void>}).setSinkId(
          selectedOutput
        )
        await audio.play().catch(() => undefined)
      }
    } catch {
      // best effort
    }
    setTimeout(() => setTonePlaying(false), 800)
  }, [selectedOutput])

  // ── Record & play back ──
  const recordAndPlayback = useCallback(async () => {
    const stream = streamRef.current
    if (!stream) return
    try {
      recordedChunks.current = []
      const recorder = new MediaRecorder(stream)
      recorderRef.current = recorder
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunks.current.push(e.data)
      }
      recorder.onstop = () => {
        setRecording(false)
        setPlayingBack(true)
        const blob = new Blob(recordedChunks.current, {type: 'audio/webm'})
        const audio = new Audio(URL.createObjectURL(blob))
        playbackAudioRef.current = audio
        if (selectedOutput && supportsSinkId()) {
          void (audio as unknown as {setSinkId: (id: string) => Promise<void>})
            .setSinkId(selectedOutput)
            .catch(() => undefined)
        }
        audio.onended = () => {
          setPlayingBack(false)
          setRecordingDone(true)
        }
        void audio.play().catch(() => {
          setPlayingBack(false)
          setRecordingDone(true)
        })
      }
      setRecording(true)
      setRecordingDone(false)
      recorder.start()
      setTimeout(() => {
        if (recorder.state !== 'inactive') recorder.stop()
      }, 4000)
    } catch {
      setRecording(false)
    }
  }, [selectedOutput])

  const micGood = micLevelDetected && micConfirmed !== false
  const speakerGood = speakerConfirmed === true

  const goTo = useCallback((next: StepIndex) => setStep(next), [])

  const stepNodes = useMemo(
    () => [
      {label: 'Microphone', done: micGood && step > 0},
      {label: 'Speaker', done: speakerGood && step > 1},
      {label: 'Ready', done: false},
    ],
    [micGood, speakerGood, step]
  )

  if (!show) return null

  return (
    <div className='audio-check' role='dialog' aria-label='Audio and device check'>
      <header className='audio-check__head'>
        <h2 className='audio-check__title'>
          <span aria-hidden='true'>🎧</span> Audio &amp; Device Check
        </h2>
        <p className='audio-check__subtitle'>
          A quick test so the first thing a customer hears is you, not silence.
        </p>
        <button type='button' className='audio-check__x' aria-label='Close' onClick={onClose}>
          ✕
        </button>
      </header>

      <nav className='audio-check__stepper'>
        {stepNodes.map((node, i) => (
          <button
            type='button'
            key={node.label}
            className={`audio-check__node${i === step ? ' is-active' : ''}${
              node.done ? ' is-done' : ''
            }`}
            onClick={() => goTo(i as StepIndex)}
          >
            <span className='audio-check__num'>{node.done ? '✓' : i + 1}</span>
            <span className='audio-check__cap'>{node.label}</span>
          </button>
        ))}
      </nav>

      <div className='audio-check__body'>
        {/* Step 1 — Microphone */}
        {step === 0 && (
          <section className='audio-check__pane'>
            <div className='audio-check__pane-head'>
              <span className='audio-check__ic' aria-hidden='true'>
                🎙️
              </span>
              <div>
                <h3>Test your microphone</h3>
                <p>This is what the customer will hear from you.</p>
              </div>
            </div>

            <div className='audio-check__field'>
              <label htmlFor='audio-check-mic'>Microphone</label>
              <select
                id='audio-check-mic'
                value={selectedInput}
                onChange={(e) => handleInputChange(e.target.value)}
              >
                <option value=''>Default microphone</option>
                {inputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Microphone ${d.deviceId.slice(0, 6)}`}
                  </option>
                ))}
              </select>
            </div>

            <p className='audio-check__prompt'>Talk normally — the bar should move as you speak.</p>
            <div className='audio-check__meter' aria-label='Live microphone level'>
              {Array.from({length: METER_SEGMENTS}).map((_, i) => (
                <span
                  key={i}
                  className='audio-check__seg'
                  ref={(el) => {
                    segRefs.current[i] = el
                  }}
                />
              ))}
            </div>

            <div className='audio-check__row'>
              <button
                type='button'
                className={`audio-check__btn${recording || playingBack ? ' is-busy' : ''}`}
                onClick={recordAndPlayback}
                disabled={micPermission === 'denied'}
              >
                <span aria-hidden='true'>{recordingDone ? '↺' : '⏺'}</span>{' '}
                {recording
                  ? 'Recording…'
                  : playingBack
                  ? 'Playing back…'
                  : recordingDone
                  ? 'Record again'
                  : 'Record & play back'}
              </button>
              {recordingDone && (
                <span className='audio-check__ask'>
                  Heard yourself clearly?
                  <span className='audio-check__yn'>
                    <button
                      type='button'
                      className={`audio-check__mini is-yes${
                        micConfirmed === true ? ' is-sel' : ''
                      }`}
                      onClick={() => setMicConfirmed(true)}
                    >
                      Yes
                    </button>
                    <button
                      type='button'
                      className={`audio-check__mini is-no${
                        micConfirmed === false ? ' is-sel' : ''
                      }`}
                      onClick={() => setMicConfirmed(false)}
                    >
                      No
                    </button>
                  </span>
                </span>
              )}
            </div>

            {(micPermission === 'prompt' || micPermission === 'unknown') && !micLevelDetected && (
              <div className='audio-check__row'>
                <button type='button' className='audio-check__btn' onClick={requestPermission}>
                  <span aria-hidden='true'>🎤</span> Grant microphone access
                </button>
              </div>
            )}
            {micPermission === 'denied' && (
              <div className='audio-check__guide'>
                <div className='audio-check__guide-head'>
                  <span aria-hidden='true'>🔒</span> Microphone is blocked for this site
                </div>
                <ol className='audio-check__guide-steps'>
                  {permissionUnblockSteps().map((stepText) => (
                    <li key={stepText}>{stepText}</li>
                  ))}
                </ol>
                <button type='button' className='audio-check__btn' onClick={requestPermission}>
                  <span aria-hidden='true'>↺</span> Try again
                </button>
              </div>
            )}
            {micLevelDetected && micConfirmed !== false && (
              <div className='audio-check__result is-ok'>
                <span aria-hidden='true'>✓</span> We can hear your microphone
              </div>
            )}
            {micConfirmed === false && (
              <div className='audio-check__result is-bad'>
                <span aria-hidden='true'>✕</span> Try another microphone from the list above
              </div>
            )}
          </section>
        )}

        {/* Step 2 — Speaker */}
        {step === 1 && (
          <section className='audio-check__pane'>
            <div className='audio-check__pane-head'>
              <span className='audio-check__ic' aria-hidden='true'>
                🔊
              </span>
              <div>
                <h3>Test your speaker</h3>
                <p>This is what you will hear from the customer.</p>
              </div>
            </div>

            <div className='audio-check__field'>
              <label htmlFor='audio-check-spk'>Speaker</label>
              <select
                id='audio-check-spk'
                value={selectedOutput}
                onChange={(e) => handleOutputChange(e.target.value)}
                disabled={!supportsSinkId()}
              >
                <option value=''>Default speaker</option>
                {outputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Speaker ${d.deviceId.slice(0, 6)}`}
                  </option>
                ))}
              </select>
              {!supportsSinkId() && (
                <p className='audio-check__note'>
                  This browser plays the test on the system default output.
                </p>
              )}
            </div>

            <div className='audio-check__row'>
              <button
                type='button'
                className={`audio-check__btn${tonePlaying ? ' is-busy' : ''}`}
                onClick={playTestTone}
              >
                <span aria-hidden='true'>▶</span> Play test sound
              </button>
              <span className='audio-check__ask'>
                Did you hear it?
                <span className='audio-check__yn'>
                  <button
                    type='button'
                    className={`audio-check__mini is-yes${
                      speakerConfirmed === true ? ' is-sel' : ''
                    }`}
                    onClick={() => setSpeakerConfirmed(true)}
                  >
                    Yes
                  </button>
                  <button
                    type='button'
                    className={`audio-check__mini is-no${
                      speakerConfirmed === false ? ' is-sel' : ''
                    }`}
                    onClick={() => setSpeakerConfirmed(false)}
                  >
                    No
                  </button>
                </span>
              </span>
            </div>

            {speakerConfirmed === true && (
              <div className='audio-check__result is-ok'>
                <span aria-hidden='true'>✓</span> Speaker is working
              </div>
            )}
            {speakerConfirmed === false && (
              <div className='audio-check__result is-bad'>
                <span aria-hidden='true'>✕</span> Try another speaker from the list above
              </div>
            )}
          </section>
        )}

        {/* Step 3 — Ready */}
        {step === 2 && (
          <section className='audio-check__pane'>
            <div className='audio-check__pane-head'>
              <span className='audio-check__ic' aria-hidden='true'>
                ✅
              </span>
              <div>
                <h3>You&apos;re set</h3>
                <p>Last checks before you take calls.</p>
              </div>
            </div>

            <div className='audio-check__checks'>
              <div className={`audio-check__check${micPermission === 'granted' ? '' : ' is-warn'}`}>
                <span className='audio-check__mark'>{micPermission === 'granted' ? '✓' : '!'}</span>
                <span>
                  <span className='audio-check__lbl'>Microphone access</span> ·{' '}
                  <span className='audio-check__sub'>
                    {micPermission === 'granted'
                      ? 'Granted'
                      : micPermission === 'denied'
                      ? 'Blocked — allow it from the address bar'
                      : 'Not granted yet'}
                  </span>
                </span>
                {micPermission !== 'granted' && (
                  <button
                    type='button'
                    className='audio-check__mini is-yes audio-check__grant'
                    onClick={requestPermission}
                  >
                    Grant
                  </button>
                )}
              </div>
              <div className={`audio-check__check${isRecommendedBrowser() ? '' : ' is-warn'}`}>
                <span className='audio-check__mark'>{isRecommendedBrowser() ? '✓' : '!'}</span>
                <span>
                  <span className='audio-check__lbl'>Browser</span> ·{' '}
                  <span className='audio-check__sub'>{browserLabel()}</span>
                </span>
              </div>
              <div className={`audio-check__check${window.isSecureContext ? '' : ' is-warn'}`}>
                <span className='audio-check__mark'>{window.isSecureContext ? '✓' : '!'}</span>
                <span>
                  <span className='audio-check__lbl'>Connection</span> ·{' '}
                  <span className='audio-check__sub'>
                    {window.isSecureContext ? 'Secure (HTTPS)' : 'Not secure'}
                  </span>
                </span>
              </div>
            </div>

            <div className={`audio-check__summary${micGood && speakerGood ? '' : ' is-pending'}`}>
              <span className='audio-check__badge'>{micGood && speakerGood ? '✓' : '!'}</span>
              <div>
                <div className='audio-check__st'>
                  {micGood && speakerGood
                    ? 'Your headset is working'
                    : micGood
                    ? 'Speaker still untested'
                    : speakerGood
                    ? 'Microphone still untested'
                    : 'Mic and speaker not tested yet'}
                </div>
                <div className='audio-check__ss'>
                  {micGood && speakerGood
                    ? 'Mic and speaker both passed.'
                    : 'You can still continue — testing is optional.'}
                </div>
              </div>
            </div>

            <p className='audio-check__tip'>
              <span aria-hidden='true'>💡</span>
              <span>
                <strong>PolicyDial uses your default device.</strong> Set the one you tested as the
                default in your system sound settings so every call uses it.
              </span>
            </p>
          </section>
        )}
      </div>

      <footer className='audio-check__foot'>
        <button type='button' className='audio-check__skip' onClick={onClose}>
          {step === 2 ? 'Close' : 'Skip for now'}
        </button>
        <span className='audio-check__spacer' />
        {step > 0 && (
          <button
            type='button'
            className='audio-check__nav is-ghost'
            onClick={() => goTo((step - 1) as StepIndex)}
          >
            Back
          </button>
        )}
        <button
          type='button'
          className='audio-check__nav is-primary'
          onClick={() => (step < 2 ? goTo((step + 1) as StepIndex) : onClose())}
        >
          {step === 2 ? 'Continue to PolicyDial' : 'Next'}
        </button>
      </footer>
    </div>
  )
}

export default AudioDeviceCheck
